import { useT } from "../hooks/useT";
import { useEffect, useState } from "react";
import { QuebecTopoMap } from "./QuebecTopoMap";
import { useMagneticHover } from "../hooks/useMagneticHover";

export default function HeroSection() {
  const t = useT();
  const [scrollY, setScrollY] = useState(0);
  const ctaRef = useMagneticHover(0.3, 80);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const heroOpacity = Math.max(0, 1 - scrollY / 600);

  const scrollTo = (e, target) => {
    e.preventDefault();
    document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative h-screen flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: '#0a0a0a' }}
    >
      {/* Layer 0: Generative SVG topographic map of Québec */}
      <QuebecTopoMap />

      {/* Layer 1: Ambient CSS Glows */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute top-1/4 left-1/4 w-[50vw] h-[50vw] bg-[var(--gold)] opacity-[0.03] blur-[120px] rounded-full mix-blend-screen" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] bg-[#3a2e20] opacity-[0.1] blur-[140px] rounded-full mix-blend-screen" />
      </div>

      {/* Layer 2: Center vignette radial gradient */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 70% 60% at 50% 50%, transparent 0%, rgba(10,10,10,0.6) 50%, rgba(10,10,10,0.92) 100%)',
      }} />

      {/* Layer 3: Subtle fine grain noise overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.3 }}>
          <defs>
            <filter id="hero-grain">
              <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" stitchTiles="stitch" seed="7" />
              <feColorMatrix type="saturate" values="0" />
              <feComponentTransfer>
                <feFuncR type="linear" slope="0.06" intercept="0.02" />
                <feFuncG type="linear" slope="0.06" intercept="0.02" />
                <feFuncB type="linear" slope="0.06" intercept="0.02" />
              </feComponentTransfer>
            </filter>
          </defs>
          <rect width="100%" height="100%" filter="url(#hero-grain)" />
        </svg>
      </div>

      {/* Layer 4: Faint cadastral grid lines */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent 120px, rgba(255,255,255,0.018) 120px, rgba(255,255,255,0.018) 121px),
          repeating-linear-gradient(90deg, transparent, transparent 160px, rgba(255,255,255,0.012) 160px, rgba(255,255,255,0.012) 161px)
        `,
      }} />

      {/* Asymmetric vertical line */}
      <div className="absolute top-0 bottom-0 left-[33.33%] w-px hidden md:block" style={{ backgroundColor: 'rgba(255,255,255,0.025)' }} />

      {/* Content: scroll fade + staggered entrance */}
      <div
        className="relative z-10 px-6"
        style={{ opacity: heroOpacity, transform: `translateY(${-scrollY * 0.03}px)` }}
      >
        <div className="hero-stagger text-center">
          <h1
            data-testid="hero-brand-name"
            className="font-serif tracking-[0.2em] text-[var(--text-primary)] uppercase"
            style={{ fontWeight: 300, lineHeight: 0.9, fontSize: 'clamp(3rem, 10vw, 8rem)', marginBottom: 'clamp(28px, 3vw, 48px)' }}
          >
            Leon Galt
          </h1>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'clamp(28px, 3vw, 48px)' }}>
            <div style={{ width: '40px', height: '1px', backgroundColor: 'var(--gold)', opacity: 0.5 }} />
          </div>

          <p
            data-testid="hero-tagline"
            className="font-sans uppercase text-[var(--text-secondary)]"
            style={{ fontSize: 'clamp(10px, 1vw, 12px)', letterSpacing: '0.3em', marginBottom: 'clamp(48px, 5vw, 80px)' }}
          >
            {t.hero.tagline}
          </p>

          <div ref={ctaRef}>
            <a
              href="#inquiry"
              onClick={(e) => scrollTo(e, "#inquiry")}
              data-testid="hero-inquiry-link"
              className="inline-block font-sans text-[11px] uppercase tracking-[0.22em] text-[var(--text-tertiary)] transition-colors duration-300 hover:text-[var(--text-primary)] group"
            >
              {t.hero.cta}
              <span className="block w-0 group-hover:w-full h-px bg-[var(--text-primary)] transition-all duration-500 ease-out mt-1.5" />
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
           style={{ opacity: Math.max(0, 0.4 - scrollY / 300) }}>
        <div className="w-px h-7" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
      </div>

      {/* Section edge bleed to warm mandate */}
      <div className="absolute bottom-0 left-0 right-0 h-[40px] pointer-events-none z-20"
        style={{ background: 'linear-gradient(to bottom, transparent, #0d0c0a)' }} />
    </section>
  );
}
